import { plugin as unleashPlugin } from '@unleash/proxy-client-vue';
import { App } from 'vue';

import { Installable } from '@rygen/rygen-ui/config/Installable';
import environmentPropertiesStore from '@rygen/rygen-ui/env/EnvironmentPropertiesStore';

export class UnleashInstaller implements Installable {
  install(app: App): void {
    const config = {
      fetch: UnleashInstaller.createContextAwareFetch(),
      url: environmentPropertiesStore.getValue('UNLEASH_URL'),
      clientKey: environmentPropertiesStore.getValue('UNLEASH_CLIENT_KEY'),
      refreshInterval: 10,
      appName: environmentPropertiesStore.getValue('UNLEASH_ENVIRONMENT_NAME'),
      context: {
        properties: {
          environmentName:
            environmentPropertiesStore.getValue('ENVIRONMENT_NAME'),
        },
      },
    };

    app.use(unleashPlugin, { config });
  }

  /**
   * Wraps fetch so the first toggle request of each context drops If-None-Match, and so cannot be answered
   * 304 — it must return a body.
   *
   * The proxy evaluates flags per context and returns only the matching ones, so a response and its ETag
   * describe one context. The SDK holds a single ETag across context changes and never clears it, so the
   * first request carrying a newly resolved User and domain gets validated against a payload for a context
   * that had neither. Two ways that ends badly, both observed in PROD: the two payloads match, the proxy
   * correctly answers 304, and the SDK keeps the flags it already had; or the ETag belongs to a payload the
   * SDK recorded but never parsed, because the request carrying it was aborted between those two steps.
   * Either way the session runs on toggles missing every domain-constrained flag, and every later poll
   * re-sends that same ETag, so it never recovers.
   *
   * Requests within an unchanged context keep their validator and are still answered 304, which is what
   * keeps the 10-second poll cheap. Only a context change pays for a full payload — plus a request that
   * replaced one still in flight, since that one's ETag may already be recorded without its body.
   *
   * State is per call rather than per class so that installing twice, as a test mounting the app does,
   * cannot carry one client's last request into another's.
   */
  private static createContextAwareFetch() {
    let lastUrl: string | undefined;
    let lastSignal: AbortSignal | undefined;

    return (url: unknown, options?: RequestInit): Promise<Response> => {
      const target = String(url);

      // Metrics posts to its own path and shares this fetch. Returning before the state below is touched
      // keeps it from alternating the recorded URL, which would read every poll as a context change.
      if (options?.method !== 'GET') {
        return window.fetch(target, options);
      }

      let init = options;

      try {
        const isNewContext = target !== lastUrl;
        // The SDK aborts the in-flight request before starting the next, so by now a replaced request
        // already reports aborted.
        const replacedARequestInFlight = lastSignal?.aborted === true;

        lastUrl = target;
        lastSignal = options?.signal ?? undefined;

        if (isNewContext || replacedARequestInFlight) {
          const headers = new Headers(options?.headers ?? undefined);
          headers.delete('If-None-Match');
          init = { ...options, headers };
        }
      } catch (e) {
        // Fallback -- Any failure sends exactly the request the SDK asked for, which is how this behaved before
        // CS-11569 — so the worst case is the bug we already have rather than no flags at all.
        // A rejection from the fetch below is not caught here: the SDK needs to see those.
        console.warn(
          '[unleash] could not drop a stale validator, sending as-is',
          e,
        );
      }

      return window.fetch(target, init);
    };
  }
}

export const unleash = new UnleashInstaller();
